import React, { useEffect, useState, useCallback } from 'react';
import { FUNCTIONS_URL } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { usePermission, PAGES, SETTINGS_TABS, SETTINGS_TAB_INFO, PAGE_INFO } from '../contexts/PermissionContext';
import PermissionTagManagement from '../components/PermissionTagManagement';
import UserPermissionModal from '../components/UserPermissionModal';
import toast from 'react-hot-toast';

// ============================================================
// 權限管理 Tab 組件 (新版)
// ============================================================
const PermissionManagementTab = () => {
  const { getAuthHeader, userProfile } = useAuth();
  const { permissionTags, hasWildcardAccess, canManageUsers, canManageTargetUser, getVisibleTags } = usePermission();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [selectedUser, setSelectedUser] = useState(null);
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);

  // 新增使用者相關狀態
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [createMode, setCreateMode] = useState('google'); // 'google' 或 'password'
  const [createFormData, setCreateFormData] = useState({
    email: '',
    password: '',
    display_name: '',
  });
  const [createLoading, setCreateLoading] = useState(false);

  // 載入使用者列表
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeader();

      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const response = await fetch(`${FUNCTIONS_URL}/api/users?${params}`, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        setUsers(result.data.users);
        setPagination(prev => ({
          ...prev,
          total: result.data.pagination.total,
        }));
      } else {
        toast.error(result.error?.message || '載入使用者列表失敗');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('載入使用者列表失敗');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filters, pagination.limit, pagination.offset]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openPermissionModal = (user) => {
    setSelectedUser(user);
    setIsPermissionModalOpen(true);
  };

  const closePermissionModal = () => {
    setSelectedUser(null);
    setIsPermissionModalOpen(false);
  };

  // 開啟新增使用者 Modal
  const openCreateModal = () => {
    setCreateFormData({
      email: '',
      password: '',
      display_name: '',
    });
    setCreateMode('google');
    setIsCreateModalOpen(true);
  };

  // 關閉新增使用者 Modal
  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateFormData({
      email: '',
      password: '',
      display_name: '',
    });
  };

  // 新增使用者
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateLoading(true);

    try {
      const headers = await getAuthHeader();

      const payload = {
        email: createFormData.email,
        display_name: createFormData.display_name || undefined,
      };

      if (createMode === 'google') {
        payload.invite_type = 'google';
      } else {
        payload.password = createFormData.password;
      }

      const response = await fetch(`${FUNCTIONS_URL}/api/users`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (result.success) {
        toast.success('使用者建立成功，可以用此帳號登入了');
        closeCreateModal();
        loadUsers();
      } else {
        toast.error(result.error?.message || '建立使用者失敗');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('建立使用者失敗');
    } finally {
      setCreateLoading(false);
    }
  };

  const getStatusBadgeClass = (status, is_active) => {
    if (status === 'suspended' || is_active === false) {
      return 'bg-red-100 text-red-800';
    }
    return 'bg-green-100 text-green-800';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('zh-TW');
  };

  // 取得用戶的權限標籤顯示
  const renderUserTags = (user) => {
    const tags = user.visible_tags || [];
    if (tags.includes('*')) {
      return (
        <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
          全部權限
        </span>
      );
    }
    if (tags.length === 0) {
      return <span className="text-gray-400 text-xs">無</span>;
    }
    return (
      <div className="flex flex-wrap gap-1">
        {tags.slice(0, 3).map((tagName) => {
          const tagInfo = permissionTags.find(t => t.name === tagName);
          return (
            <span
              key={tagName}
              className="px-2 py-0.5 text-xs rounded text-white"
              style={{ backgroundColor: tagInfo?.color || '#6B7280' }}
            >
              {tagName}
            </span>
          );
        })}
        {tags.length > 3 && (
          <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600">
            +{tags.length - 3}
          </span>
        )}
      </div>
    );
  };

  // 取得用戶的頁面權限顯示（固定順序：專案、報表、設定）
  const renderUserPages = (user) => {
    const pages = user.page_permissions || {};
    // 使用固定順序
    const orderedPageIds = [PAGES.PROJECTS, PAGES.REPORTS, PAGES.SETTINGS];
    const enabledPages = orderedPageIds.filter((pageId) => pages[pageId]?.enabled);

    if (enabledPages.length === 0) {
      return <span className="text-gray-400 text-xs">無</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {enabledPages.map((pageId) => {
          const pageInfo = PAGE_INFO[pageId];
          return (
            <span
              key={pageId}
              className="px-2 py-1 text-base rounded bg-blue-100 text-blue-800"
              title={pageInfo?.label}
            >
              {pageInfo?.icon}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">權限管理</h2>
          <p className="text-sm text-gray-500">管理使用者的頁面存取權限與資料標籤</p>
        </div>
        {canManageUsers() && (
          <button
            onClick={openCreateModal}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
          >
            <span>+</span>
            <span>新增使用者</span>
          </button>
        )}
      </div>

      {/* 篩選器 */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="搜尋 Email 或名稱..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          </div>
          <div>
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
            >
              <option value="">全部狀態</option>
              <option value="active">啟用</option>
              <option value="suspended">停用</option>
            </select>
          </div>
          <button
            onClick={loadUsers}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            重新整理
          </button>
        </div>
      </div>

      {/* 使用者列表 */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="text-center py-8 text-gray-500">載入中...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-500">沒有使用者資料</div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">使用者</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">頁面權限</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">資料標籤</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users
                // 非管理員在列表中隱藏自己
                .filter(user => hasWildcardAccess() || user.uid !== userProfile?.uid)
                .map((user) => (
                <tr key={user.uid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center font-medium">
                        {(user.display_name || user.email || '?')[0].toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.display_name || '-'}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(user.status, user.is_active)}`}>
                      {user.status === 'suspended' || user.is_active === false ? '停用' : '啟用'}
                    </span>
                    {user.page_permissions?.settings?.tabs?.includes('permission_management') && (
                      <span className="ml-2 px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-800">
                        可管理用戶
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {renderUserPages(user)}
                  </td>
                  <td className="px-6 py-4">
                    {renderUserTags(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {(() => {
                      const isSelf = user.uid === userProfile?.uid;
                      const isAdmin = hasWildcardAccess();
                      const isCreatedByMe = user.created_by === userProfile?.uid;

                      // 管理員可以管理任何人（包括自己）
                      if (isAdmin) {
                        return (
                          <button
                            onClick={() => openPermissionModal(user)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            編輯權限
                          </button>
                        );
                      }

                      // 非管理員不能管理自己
                      if (isSelf) {
                        return (
                          <span className="text-gray-400 text-xs" title="無法編輯自己的權限">
                            無法編輯
                          </span>
                        );
                      }

                      // 只能管理自己創建的用戶
                      if (isCreatedByMe) {
                        return (
                          <button
                            onClick={() => openPermissionModal(user)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            編輯權限
                          </button>
                        );
                      }

                      // 其他情況顯示無法編輯
                      if (canManageUsers()) {
                        return (
                          <span className="text-gray-400 text-xs" title="只能管理自己創建的用戶">
                            無法編輯
                          </span>
                        );
                      }
                      return null;
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 分頁 */}
        {pagination.total > pagination.limit && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              顯示 {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} 筆，共 {pagination.total} 筆
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={pagination.offset === 0}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                上一頁
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={pagination.offset + pagination.limit >= pagination.total}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 權限編輯 Modal */}
      <UserPermissionModal
        user={selectedUser}
        isOpen={isPermissionModalOpen}
        onClose={closePermissionModal}
        onSuccess={loadUsers}
      />

      {/* 新增使用者 Modal */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeCreateModal}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">新增使用者</h3>
              <button onClick={closeCreateModal} className="p-1 hover:bg-gray-100 rounded transition">
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 模式切換 */}
            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setCreateMode('google')}
                className={`flex-1 py-2 px-4 rounded-lg border transition ${
                  createMode === 'google'
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                Google 帳戶邀請
              </button>
              <button
                type="button"
                onClick={() => setCreateMode('password')}
                className={`flex-1 py-2 px-4 rounded-lg border transition ${
                  createMode === 'password'
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                密碼建立
              </button>
            </div>

            {createMode === 'google' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  輸入使用者的 Google 帳戶 Email，使用者首次用 Google 登入時將自動獲得權限。
                </p>
              </div>
            )}

            <form onSubmit={handleCreateUser}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電子郵件 *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    value={createFormData.email}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({ ...prev, email: e.target.value }))
                    }
                  />
                </div>

                {createMode === 'password' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      密碼 *
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      placeholder="至少 6 個字元"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      value={createFormData.password}
                      onChange={(e) =>
                        setCreateFormData((prev) => ({ ...prev, password: e.target.value }))
                      }
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    顯示名稱
                  </label>
                  <input
                    type="text"
                    placeholder="選填"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    value={createFormData.display_name}
                    onChange={(e) =>
                      setCreateFormData((prev) => ({ ...prev, display_name: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                >
                  {createLoading ? '處理中...' : '建立使用者'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// 系統設定 Tab 組件
// ============================================================
const SystemTab = () => {
  const { getAuthHeader } = useAuth();
  const [authStatus, setAuthStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // Log 清理設定狀態
  const [logRetention, setLogRetention] = useState({
    enabled: true,
    retention_days: 90,
    last_cleanup_at: null,
    last_cleanup_result: null,
  });
  const [logRetentionLoading, setLogRetentionLoading] = useState(true);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [savingLogRetention, setSavingLogRetention] = useState(false);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  useEffect(() => {
    loadLogRetention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 載入 log 保留設定
  const loadLogRetention = async () => {
    try {
      setLogRetentionLoading(true);
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/monitoring/log-retention`, {
        headers,
      });
      const data = await response.json();
      if (data.success) {
        setLogRetention(data.data);
      }
    } catch (error) {
      console.error('載入 log 保留設定失敗:', error);
    } finally {
      setLogRetentionLoading(false);
    }
  };

  // 更新 log 保留設定
  const updateLogRetention = async (updates) => {
    try {
      setSavingLogRetention(true);
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/monitoring/log-retention`, {
        method: 'PUT',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });
      const data = await response.json();
      if (data.success) {
        toast.success('設定已更新');
        setLogRetention(prev => ({ ...prev, ...data.data }));
      } else {
        toast.error(data.error || '更新失敗');
      }
    } catch (error) {
      console.error('更新 log 保留設定失敗:', error);
      toast.error('更新失敗');
    } finally {
      setSavingLogRetention(false);
    }
  };

  // 手動觸發 log 清理
  const handleManualCleanup = async () => {
    if (!confirm(`確定要立即清理 ${logRetention.retention_days} 天前的所有紀錄嗎？此操作無法復原。`)) {
      return;
    }
    try {
      setCleanupLoading(true);
      const headers = await getAuthHeader();
      const response = await fetch(`${FUNCTIONS_URL}/api/monitoring/cleanup-logs`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ retention_days: logRetention.retention_days }),
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        loadLogRetention(); // 重新載入設定以取得最後清理時間
      } else {
        toast.error(data.error || '清理失敗');
      }
    } catch (error) {
      console.error('手動清理 log 失敗:', error);
      toast.error('清理失敗');
    } finally {
      setCleanupLoading(false);
    }
  };

  const checkAuthStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${FUNCTIONS_URL}/auth/status`);
      const data = await response.json();
      setAuthStatus(data);
    } catch (error) {
      console.error('檢查授權狀態失敗:', error);
      setAuthStatus({
        success: false,
        authorized: false,
        message: '無法檢查授權狀態'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAuthorize = () => {
    window.location.href = `${FUNCTIONS_URL}/auth/authorize`;
  };

  const handleManualTrigger = async (type) => {
    const setLoading = type === 'reminders' ? setReminderLoading : setSyncLoading;
    try {
      setLoading(true);
      const response = await fetch(`${FUNCTIONS_URL}/api/scheduler/trigger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ type })
      });

      const data = await response.json();

      if (data.success) {
        toast.success(`${type === 'reminders' ? '提醒檢查' : '同步任務'}執行成功！`);
      } else {
        throw new Error(data.error || '執行失敗');
      }
    } catch (error) {
      console.error('手動觸發失敗:', error);
      toast.error(`執行失敗：${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-500">載入中...</div>;
  }

  return (
    <div className="space-y-6">
      {/* OAuth 授權狀態 */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">Apps Script 自動部署授權</h2>

        <div className={`p-4 rounded-lg mb-4 ${
          authStatus?.authorized
            ? 'bg-green-50 border border-green-200'
            : 'bg-yellow-50 border border-yellow-200'
        }`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">
              {authStatus?.authorized ? '✓' : '!'}
            </span>
            <div>
              <p className="font-semibold">
                {authStatus?.authorized ? '已授權' : '未授權'}
              </p>
              <p className="text-sm text-gray-600">
                {authStatus?.message}
              </p>
            </div>
          </div>
        </div>

        {!authStatus?.authorized && (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">為什麼需要授權？</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>系統需要您的授權才能自動部署 Apps Script 到 Google Sheets</li>
                <li>這是一次性授權，完成後系統可以自動管理所有表單的腳本</li>
                <li>授權後，當您建立新表單時，系統會自動設定自動同步功能</li>
              </ul>
            </div>

            <button
              onClick={handleAuthorize}
              className="btn-primary w-full"
            >
              開始授權
            </button>
          </div>
        )}

        {authStatus?.authorized && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">授權已完成</h3>
              <p className="text-sm text-green-800">
                系統已獲得授權，可以自動部署 Apps Script。
                當您建立新的表單範本時，系統會自動設定自動同步功能。
              </p>
            </div>

            <div className="flex gap-2">
              <button
                onClick={checkAuthStatus}
                className="btn-secondary flex-1"
              >
                重新檢查狀態
              </button>
              <button
                onClick={handleAuthorize}
                className="btn-secondary flex-1"
              >
                重新授權
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 排程任務手動觸發 */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">排程任務管理</h2>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-blue-900 mb-2">開發測試功能</h3>
          <p className="text-sm text-blue-800 mb-2">
            正常情況下，系統每天自動執行以下任務：
          </p>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>09:00 - 檢查並發送提醒郵件（截止日期提醒、逾期提醒等）</li>
            <li>09:30 - 同步所有專案問卷的 Google Sheets 資料</li>
          </ul>
        </div>

        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => handleManualTrigger('reminders')}
              disabled={reminderLoading}
              className="flex-1 bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition font-semibold"
            >
              {reminderLoading ? '執行中...' : '立即檢查並發送提醒'}
            </button>
            <button
              onClick={() => handleManualTrigger('sync')}
              disabled={syncLoading}
              className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition font-semibold"
            >
              {syncLoading ? '執行中...' : '批量同步所有問卷'}
            </button>
          </div>
        </div>
      </div>

      {/* Log 清理設定 */}
      <div className="card">
        <h2 className="text-xl font-semibold mb-4">紀錄清理設定</h2>

        {logRetentionLoading ? (
          <div className="text-center py-4 text-gray-500">載入中...</div>
        ) : (
          <>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h3 className="font-semibold text-blue-900 mb-2">自動清理說明</h3>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>系統會自動清理超過保留天數的紀錄，避免資料無限增長</li>
                <li>清理範圍：系統紀錄、提醒紀錄、專案同步紀錄</li>
                <li>排程時間：每週日凌晨 3:00</li>
              </ul>
            </div>

            {/* 啟用/停用開關 */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-800">自動清理排程</p>
                <p className="text-sm text-gray-500">啟用後系統會每週自動清理舊紀錄</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={logRetention.enabled}
                  onChange={(e) => updateLogRetention({ enabled: e.target.checked })}
                  disabled={savingLogRetention}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            {/* 保留天數設定 */}
            <div className="flex items-center justify-between py-4 border-b border-gray-200">
              <div>
                <p className="font-medium text-gray-800">資料保留天數</p>
                <p className="text-sm text-gray-500">超過此天數的紀錄將被清理</p>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={logRetention.retention_days}
                  onChange={(e) => updateLogRetention({ retention_days: parseInt(e.target.value) })}
                  disabled={savingLogRetention}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value={30}>30 天</option>
                  <option value={60}>60 天</option>
                  <option value={90}>90 天</option>
                  <option value={180}>180 天</option>
                  <option value={365}>365 天</option>
                </select>
              </div>
            </div>

            {/* 最後清理資訊 */}
            {logRetention.last_cleanup_at && (
              <div className="py-4 border-b border-gray-200">
                <p className="font-medium text-gray-800 mb-2">最後清理</p>
                <div className="text-sm text-gray-600 space-y-1">
                  <p>時間：{new Date(logRetention.last_cleanup_at).toLocaleString('zh-TW')}</p>
                  {logRetention.last_cleanup_result && (
                    <p>已清理：{logRetention.last_cleanup_result.total_deleted} 筆紀錄</p>
                  )}
                </div>
              </div>
            )}

            {/* 手動清理按鈕 */}
            <div className="pt-4">
              <button
                onClick={handleManualCleanup}
                disabled={cleanupLoading}
                className="w-full bg-red-500 hover:bg-red-600 disabled:bg-gray-300 text-white px-4 py-3 rounded-lg transition font-semibold"
              >
                {cleanupLoading ? '清理中...' : `立即清理 ${logRetention.retention_days} 天前的紀錄`}
              </button>
              <p className="text-xs text-gray-500 mt-2 text-center">
                此操作會立即刪除超過 {logRetention.retention_days} 天的所有紀錄，無法復原
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// 系統紀錄 Tab 組件 - 表格形式
// ============================================================
const SystemLogsTab = () => {
  const { getAuthHeader } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0, has_more: false });
  const [filters, setFilters] = useState({
    activity_type: '',
    start_date: '',
    end_date: ''
  });

  // 活動類型對照表
  const ACTIVITY_TYPE_LABELS = {
    'auth': { label: '認證', color: 'bg-purple-100 text-purple-800' },
    'user': { label: '用戶管理', color: 'bg-blue-100 text-blue-800' },
    'project': { label: '專案全局', color: 'bg-green-100 text-green-800' },
    'vendor': { label: '供應商', color: 'bg-orange-100 text-orange-800' },
    'form': { label: '問卷', color: 'bg-yellow-100 text-yellow-800' },
    'response': { label: '回覆', color: 'bg-cyan-100 text-cyan-800' },
    'reminder': { label: '提醒', color: 'bg-pink-100 text-pink-800' },
    'sync': { label: '同步', color: 'bg-indigo-100 text-indigo-800' },
    'permission': { label: '權限', color: 'bg-red-100 text-red-800' },
    'system': { label: '系統', color: 'bg-gray-100 text-gray-800' },
  };

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeader();

      const params = new URLSearchParams();
      if (filters.activity_type) params.append('activity_type', filters.activity_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', pagination.limit);
      params.append('offset', pagination.offset);

      const response = await fetch(`${FUNCTIONS_URL}/api/activity-logs?${params}`, {
        headers: {
          ...headers,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        setLogs(result.data.logs || []);
        setPagination(prev => ({
          ...prev,
          total: result.data.pagination?.total || 0,
          has_more: result.data.pagination?.has_more || false,
        }));
      } else {
        toast.error(result.error?.message || '載入系統紀錄失敗');
      }
    } catch (error) {
      console.error('Error loading system logs:', error);
      toast.error('載入系統紀錄失敗');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, filters, pagination.limit, pagination.offset]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActivityTypeInfo = (type) => {
    return ACTIVITY_TYPE_LABELS[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">系統紀錄</h2>
          <p className="text-sm text-gray-500">查看登入、認證及全局操作紀錄</p>
        </div>
        <button
          onClick={loadLogs}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition flex items-center gap-2"
        >
          <span>重新整理</span>
        </button>
      </div>

      {/* 篩選器 */}
      <div className="card mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">活動類型</label>
            <select
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={filters.activity_type}
              onChange={(e) => handleFilterChange('activity_type', e.target.value)}
            >
              <option value="">全部類型</option>
              {Object.entries(ACTIVITY_TYPE_LABELS).map(([key, info]) => (
                <option key={key} value={key}>{info.label}</option>
              ))}
            </select>
          </div>
          <div className="min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">開始日期</label>
            <input
              type="date"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={filters.start_date}
              onChange={(e) => handleFilterChange('start_date', e.target.value)}
            />
          </div>
          <div className="min-w-[160px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">結束日期</label>
            <input
              type="date"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              value={filters.end_date}
              onChange={(e) => handleFilterChange('end_date', e.target.value)}
            />
          </div>
          <button
            onClick={() => {
              setFilters({ activity_type: '', start_date: '', end_date: '' });
              setPagination(prev => ({ ...prev, offset: 0 }));
            }}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition"
          >
            清除篩選
          </button>
        </div>
      </div>

      {/* 紀錄表格 */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4" />
            <p className="text-gray-500">載入中...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">沒有系統紀錄</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">時間</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">類型</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">描述</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">操作者</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {logs.map((log, index) => {
                  const typeInfo = getActivityTypeInfo(log.activity_type);
                  return (
                    <tr key={log.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                        {formatDate(log.performed_at)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${typeInfo.color}`}>
                            {typeInfo.label}
                          </span>
                          {log.project_code && (
                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                              {log.project_code}
                            </span>
                          )}
                          {log.is_global && (
                            <span className="text-xs text-purple-600 bg-purple-50 px-2 py-0.5 rounded">
                              全局
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                        <div className="truncate" title={log.description}>
                          {log.description}
                        </div>
                        {log.changes && (
                          <details className="mt-1">
                            <summary className="text-xs text-purple-600 cursor-pointer hover:text-purple-800">
                              查看變更詳情
                            </summary>
                            <pre className="mt-1 p-2 bg-gray-100 rounded text-xs overflow-x-auto max-w-sm">
                              {JSON.stringify(log.changes, null, 2)}
                            </pre>
                          </details>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{log.performed_by_name || '系統'}</div>
                        {log.ip_address && (
                          <div className="text-xs text-gray-500">{log.ip_address}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* 分頁 */}
        {(pagination.total > pagination.limit || pagination.offset > 0) && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              顯示 {pagination.offset + 1} - {Math.min(pagination.offset + logs.length, pagination.total)} 筆
              {pagination.total > 0 && `，共 ${pagination.total} 筆`}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={pagination.offset === 0}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
              >
                上一頁
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={!pagination.has_more}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-100"
              >
                下一頁
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// 主要 Settings 組件
// ============================================================
const Settings = () => {
  const { canAccessSettingsTab, hasWildcardAccess } = usePermission();
  const [activeTab, setActiveTab] = useState(null);

  // 根據權限決定可用的分頁
  const tabs = [];

  // 權限管理 - 需要 permission_management tab 權限
  if (canAccessSettingsTab(SETTINGS_TABS.PERMISSION_MANAGEMENT)) {
    tabs.push({
      id: SETTINGS_TABS.PERMISSION_MANAGEMENT,
      ...SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_MANAGEMENT],
    });
  }

  // 權限標籤管理 - 管理員或有勾選此 Tab 權限的用戶
  if (canAccessSettingsTab(SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT)) {
    tabs.push({
      id: SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT,
      ...SETTINGS_TAB_INFO[SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT],
    });
  }

  // 系統設定 - 僅管理員 (visible_tags: ["*"])
  if (hasWildcardAccess()) {
    tabs.push({
      id: SETTINGS_TABS.SYSTEM_CONFIG,
      ...SETTINGS_TAB_INFO[SETTINGS_TABS.SYSTEM_CONFIG],
    });
  }

  // 系統紀錄 - 僅管理員 (visible_tags: ["*"])
  if (hasWildcardAccess()) {
    tabs.push({
      id: SETTINGS_TABS.SYSTEM_LOGS,
      ...SETTINGS_TAB_INFO[SETTINGS_TABS.SYSTEM_LOGS],
    });
  }

  // 如果當前 tab 不在可用列表中，切換到第一個可用 tab
  useEffect(() => {
    if (tabs.length > 0 && (!activeTab || !tabs.find(t => t.id === activeTab))) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs, activeTab]);

  // 如果沒有任何可用 tab，顯示無權限訊息
  if (tabs.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-16 text-gray-500">
          您沒有權限存取設定頁面
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">設定</h1>

      {/* 分頁導航 */}
      {tabs.length > 1 && (
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* 分頁內容 */}
      {activeTab === SETTINGS_TABS.PERMISSION_MANAGEMENT && <PermissionManagementTab />}
      {activeTab === SETTINGS_TABS.PERMISSION_TAG_MANAGEMENT && <PermissionTagManagement />}
      {activeTab === SETTINGS_TABS.SYSTEM_CONFIG && <SystemTab />}
      {activeTab === SETTINGS_TABS.SYSTEM_LOGS && <SystemLogsTab />}
    </div>
  );
};

export default Settings;
